import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
type ViewMode = 'my-specialty' | 'all' | 'compare';
interface CrossSpecialtyComparisonProps {
  viewMode: ViewMode;
  onContinue: () => void;
}
const segmentDistribution = [{
  specialty: 'Cardiologist',
  'Evidence Optimizer': 38,
  'Pragmatic Adopter': 32,
  'Innovation Seeker': 18,
  'Guideline Follower': 12
}, {
  specialty: 'Nephrologist',
  'Evidence Optimizer': 42,
  'Pragmatic Adopter': 28,
  'Innovation Seeker': 15,
  'Guideline Follower': 15
}, {
  specialty: 'GP',
  'Evidence Optimizer': 22,
  'Pragmatic Adopter': 45,
  'Innovation Seeker': 8,
  'Guideline Follower': 25
}, {
  specialty: 'Endocrinologist',
  'Evidence Optimizer': 35,
  'Pragmatic Adopter': 30,
  'Innovation Seeker': 22,
  'Guideline Follower': 13
}];
const COLORS = {
  'Evidence Optimizer': '#0D9488',
  'Pragmatic Adopter': '#6366F1',
  'Innovation Seeker': '#F59E0B',
  'Guideline Follower': '#10B981'
};
const insights = [{
  color: 'bg-teal-500',
  text: 'Specialists show higher Evidence Optimizer representation'
}, {
  color: 'bg-indigo-500',
  text: 'GPs are predominantly Pragmatic Adopters'
}, {
  color: 'bg-amber-500',
  text: 'Innovation Seekers are most common among Endocrinologists'
}];
export function CrossSpecialtyComparison({
  viewMode,
  onContinue
}: CrossSpecialtyComparisonProps) {
  return <div className="w-full min-h-screen flex items-start justify-center bg-[#FAFAF8] px-6 py-16 overflow-y-auto" data-id="element-20">
      <div className="max-w-2xl w-full space-y-6" data-id="element-21">
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-2" data-id="element-22">
          <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full uppercase tracking-wide" data-id="element-23">
            Cross-specialty view
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1C1917]" data-id="element-24">
            How different specialists think
          </h2>
          <p className="text-sm text-[#57534E]" data-id="element-25">
            Segment distribution reveals distinct patterns across specialties
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-100" data-id="element-26">
          <ResponsiveContainer width="100%" height={320} data-id="element-27">
            <BarChart data={segmentDistribution} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 5
          }} data-id="element-28">
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} data-id="element-29" />
              <XAxis dataKey="specialty" tick={{
              fill: '#57534E',
              fontSize: 13,
              fontWeight: 500
            }} axisLine={false} tickLine={false} data-id="element-30" />
              <YAxis tick={{
              fill: '#57534E',
              fontSize: 12
            }} axisLine={false} tickLine={false} data-id="element-31" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #F3F4F6',
              borderRadius: '12px',
              padding: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
            }} data-id="element-32" />
              <Legend wrapperStyle={{
              paddingTop: '16px',
              fontSize: '12px'
            }} iconType="circle" iconSize={8} data-id="element-33" />
              <Bar dataKey="Evidence Optimizer" stackId="a" fill={COLORS['Evidence Optimizer']} data-id="element-34" />
              <Bar dataKey="Pragmatic Adopter" stackId="a" fill={COLORS['Pragmatic Adopter']} data-id="element-35" />
              <Bar dataKey="Innovation Seeker" stackId="a" fill={COLORS['Innovation Seeker']} data-id="element-36" />
              <Bar dataKey="Guideline Follower" stackId="a" fill={COLORS['Guideline Follower']} radius={[4, 4, 0, 0]} data-id="element-37" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} className="space-y-3" data-id="element-38">
          {insights.map((insight, i) => <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-neutral-100" data-id="element-39">
              <span className={`flex-shrink-0 w-2.5 h-2.5 rounded-full ${insight.color} mt-1.5`} data-id="element-40" />
              <span className="text-sm text-[#1C1917] font-medium" data-id="element-41">
                {insight.text}
              </span>
            </div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="flex justify-center pt-4 pb-20" data-id="element-42">
          <button onClick={onContinue} className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#1C1917] text-white font-bold rounded-xl hover:bg-neutral-800 transition-all shadow-md hover:-translate-y-0.5" data-id="element-43">
            Continue
            <ArrowRightIcon className="w-5 h-5" data-id="element-44" />
          </button>
        </motion.div>
      </div>
    </div>;
}